import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';
import axios from 'axios';
function AdminAddStudent() {
  const apiUrl = process.env.REACT_APP_API_URL;
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [formData, setFormData] = useState({
    studentid: '',
    firstName: '',
    lastName: '',
    aadharNo: '',
    mobile: '',
    email: '',
    address: '',
    postcode: '',
    mainTelephone: '',
    degree: '',
    engineering: '',
    currentStatus: '',
    preferableCourse: '',
    batch: '',
    username: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // Log component mount and session data
  useEffect(() => {
    console.log('AdminAddStudent component mounted at route:', location.pathname);
    const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
    console.log('Checking sessionStorage for adminSession:', sessionData);
    if (!apiUrl) {
      console.error('REACT_APP_API_URL is not defined in environment variables');
      setApiError('API URL is not configured. Please contact the administrator.');
    }
    return () => console.log('AdminAddStudent component unmounted');
  }, [apiUrl, location.pathname]);
  // Fetch courses on mount
  useEffect(() => {
    const fetchCourses = async () => {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      const token = sessionData?.token;
      console.log('Token for courses fetch:', token ? 'Present' : 'Missing');
      if (!token) {
        console.log('No adminSession token, redirecting to /admin-login');
        setApiError('No authentication token found. Please log in.');
        navigate('/admin-login', { state: { fromProtected: true } });
        return;
      }
      if (!apiUrl) {
        console.error('No API URL, cannot fetch courses');
        setApiError('API URL is not configured. Please contact the administrator.');
        return;
      }
      setIsLoading(true);
      try {
        console.log('Fetching courses from:', `${apiUrl}/api/admin/courses`);
        const response = await axios.get(`${apiUrl}/api/admin/courses`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Courses API response:', response.data);
        if (response.data.success) {
          setCourses(response.data.courses);
          if (response.data.courses.length === 0) {
            setApiError('No active courses found. Please add a course first.');
          }
        } else {
          setApiError(response.data.message || 'Failed to load courses');
        }
      } catch (error) {
        console.error('Error fetching courses:', {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data
        });
        if (error.response?.status === 401) {
          console.log('401 error, redirecting to /admin-login');
          setApiError('Session expired. Please log in again.');
          sessionStorage.removeItem('adminSession');
          navigate('/admin-login', { state: { fromProtected: true } });
        } else {
          setApiError(
            error.response?.data?.message ||
            `Failed to load courses: ${error.message}`
          );
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchCourses();
  }, [navigate, apiUrl]);
  // Fetch batches when preferableCourse changes
  useEffect(() => {
    if (formData.preferableCourse) {
      const fetchBatches = async () => {
        const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
        const token = sessionData?.token;
        console.log('Token for batches fetch:', token ? 'Present' : 'Missing');
        if (!token) {
          console.log('No adminSession token, redirecting to /admin-login');
          setApiError('No authentication token found. Please log in.');
          navigate('/admin-login', { state: { fromProtected: true } });
          return;
        }
        if (!apiUrl) {
          console.error('No API URL, cannot fetch batches');
          setApiError('API URL is not configured. Please contact the administrator.');
          return;
        }
        setIsLoading(true);
        try {
          console.log('Fetching batches from:', `${apiUrl}/api/admin/batches/${formData.preferableCourse}`);
          const response = await axios.get(
            `${apiUrl}/api/admin/batches/${formData.preferableCourse}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          console.log('Batches API response:', response.data);
          if (response.data.success) {
            setBatches(response.data.batches);
            if (response.data.batches.length === 0) {
              setApiError('No active batches available for this course. Please contact the administrator to add batches.');
            }
          } else {
            setApiError(response.data.message || 'Failed to load batches');
          }
        } catch (error) {
          console.error('Error fetching batches:', {
            message: error.message,
            status: error.response?.status,
            data: error.response?.data
          });
          if (error.response?.status === 401) {
            console.log('401 error, redirecting to /admin-login');
            setApiError('Session expired. Please log in again.');
            sessionStorage.removeItem('adminSession');
            navigate('/admin-login', { state: { fromProtected: true } });
          } else if (error.response?.status === 404) {
            setApiError('Course not found. Please select a valid course.');
          } else {
            setApiError(
              error.response?.data?.message ||
              `Failed to load batches: ${error.message}`
            );
          }
        } finally {
          setIsLoading(false);
        }
      };
      fetchBatches();
    } else {
      setBatches([]);
      setApiError('');
    }
  }, [formData.preferableCourse, navigate, apiUrl]);
  // Handle form input changes
  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
      // Changing the course invalidates whatever batch was previously
      // selected (batches belong to one course each) — without this the
      // stale batch id stayed in state and got submitted alongside the new
      // course, producing a mismatched enrollment row.
      ...(id === 'preferableCourse' && value !== prev.preferableCourse ? { batch: '' } : {})
    }));
    setErrors((prev) => ({ ...prev, [id]: '' }));
    setApiError('');
  };
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    const requiredFields = [
      { key: 'firstName', label: 'First Name' },
      { key: 'lastName', label: 'Last Name' },
      { key: 'mobile', label: 'Mobile' },
      { key: 'email', label: 'Email' },
      { key: 'address', label: 'Address' }
    ];
    requiredFields.forEach(({ key, label }) => {
      if (!formData[key] || formData[key].trim().length === 0) {
        newErrors[key] = `${label} is required and cannot be empty`;
      }
    });
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (formData.mobile && !/^\d{10,15}$/.test(formData.mobile)) {
      newErrors.mobile = 'Mobile number must be 10-15 digits';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
    const token = sessionData?.token;
    console.log('Token for submit:', token ? 'Present' : 'Missing');
    if (!token) {
      console.log('No adminSession token, redirecting to /admin-login');
      setApiError('No authentication token found. Please log in.');
      navigate('/admin-login', { state: { fromProtected: true } });
      return;
    }
    if (!apiUrl) {
      console.error('No API URL, cannot submit student');
      setApiError('API URL is not configured. Please contact the administrator.');
      return;
    }
    setIsLoading(true);
    try {
      console.log('Submitting student to:', `${apiUrl}/api/admin/students`);
      const response = await axios.post(
        `${apiUrl}/api/admin/students`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Submit student API response:', response.data);
      if (response.data.success) {
        setSuccessMessage(response.data.allocationWarning || response.data.message);
        setFormData({
          studentid: '',
          firstName: '',
          lastName: '',
          aadharNo: '',
          mobile: '',
          email: '',
          address: '',
          postcode: '',
          mainTelephone: '',
          degree: '',
          engineering: '',
          currentStatus: '',
          preferableCourse: '',
          batch: '',
          username: '',
          password: ''
        });
        setErrors({});
        setApiError('');
      } else {
        setApiError(response.data.message || 'Failed to add student');
      }
    } catch (error) {
      console.error('Error adding student:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      if (error.response?.status === 401) {
        console.log('401 error, redirecting to /admin-login');
        setApiError('Session expired. Please log in again.');
        sessionStorage.removeItem('adminSession');
        navigate('/admin-login', { state: { fromProtected: true } });
      } else {
        setApiError(
          error.response?.data?.message ||
          `Failed to add student: ${error.message}`
        );
      }
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li>
                  <Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">
                    Home
                  </Link>
                </li>
                <li>
                  <span className="text-gray-500 fw-normal d-flex">
                    <i className="ph ph-caret-right" />
                  </span>
                </li>
                <li>
                  <span className="text-main-600 fw-normal text-15">Add Student</span>
                </li>
              </ul>
            </div>
            <div className="flex-align justify-content-end gap-8">
             
            </div>
          </div>
          <ul className="step-list mb-24">
            <li className="step-list__item py-15 px-24 text-15 text-heading fw-medium flex-center gap-6 active">
              <span className="icon text-xl d-flex">
                {/* <i className="ph ph-circle" /> */}
              </span>
             <h3>Student Details</h3>
              <span className="line position-relative" />
            </li>
          </ul>
          <div className="card">
            <div className="card-header border-bottom border-gray-100 flex-align gap-8">
              <h5 className="mb-0">Add New Student</h5>
              <button
                type="button"
                className="text-main-600 text-md d-flex"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                data-bs-title="Add Student Details"
              >
                <i className="ph-fill ph-question" />
              </button>
            </div>
            <div className="card-body">
              {isLoading && <div className="alert alert-info">Loading...</div>}
              {apiError && <div className="alert alert-danger">{apiError}</div>}
              {successMessage && <div className="alert alert-success">{successMessage}</div>}
              <form onSubmit={handleSubmit}>
                <div className="row gy-20">
                  <div className="col-xxl-12">
                    <h6 className="mb-20 fw-semibold font-heading">Student Information</h6>
                    <div className="row g-20">
                      <div className="col-sm-6">
                        <label htmlFor="studentid" className="h5 mb-8 fw-semibold font-heading">
                          Student ID
                        </label>
                        <input
                          type="text"
                          className="form-control py-11"
                          id="studentid"
                          placeholder="Enter Student ID"
                          value={formData.studentid}
                          onChange={handleChange}
                          disabled={isLoading}
                        />
                        {errors.studentid && <div className="text-danger text-13">{errors.studentid}</div>}
                      </div>
                      <div className="col-sm-6">
                        <label htmlFor="firstName" className="h5 mb-8 fw-semibold font-heading">
                          First Name <span className="text-13 text-red-500 fw-medium">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control py-11"
                          id="firstName"
                          placeholder="Enter First Name"
                          value={formData.firstName}
                          onChange={handleChange}
                          required
                          disabled={isLoading}
                        />
                        {errors.firstName && <div className="text-danger text-13">{errors.firstName}</div>}
                      </div>
                      <div className="col-sm-6">
                        <label htmlFor="lastName" className="h5 mb-8 fw-semibold font-heading">
                          Last Name <span className="text-13 text-red-500 fw-medium">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control py-11"
                          id="lastName"
                          placeholder="Enter Last Name"
                          value={formData.lastName}
                          onChange={handleChange}
                          required
                          disabled={isLoading}
                        />
                        {errors.lastName && <div className="text-danger text-13">{errors.lastName}</div>}
                      </div>
                      <div className="col-sm-6">
                        <label htmlFor="aadharNo" className="h5 mb-8 fw-semibold font-heading">
                          Aadhar No./Emirates ID
                        </label>
                        <input
                          type="text"
                          className="form-control py-11"
                          id="aadharNo"
                          placeholder="Enter Aadhar No. or Emirates ID"
                          value={formData.aadharNo}
                          onChange={handleChange}
                          disabled={isLoading}
                        />
                        {errors.aadharNo && <div className="text-danger text-13">{errors.aadharNo}</div>}
                      </div>
                      <div className="col-sm-6">
                        <label htmlFor="mobile" className="h5 mb-8 fw-semibold font-heading">
                          Mobile <span className="text-13 text-red-500 fw-medium">*</span>
                        </label>
                        <input
                          type="tel"
                          className="form-control py-11"
                          id="mobile"
                          placeholder="Enter 10-15 digit Mobile Number"
                          value={formData.mobile}
                          onChange={handleChange}
                          required
                          disabled={isLoading}
                        />
                        {errors.mobile && <div className="text-danger text-13">{errors.mobile}</div>}
                      </div>
                      <div className="col-sm-6">
                        <label htmlFor="email" className="h5 mb-8 fw-semibold font-heading">
                          Email <span className="text-13 text-red-500 fw-medium">*</span>
                        </label>
                        <input
                          type="email"
                          className="form-control py-11"
                          id="email"
                          placeholder="Enter Email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          disabled={isLoading}
                        />
                        {errors.email && <div className="text-danger text-13">{errors.email}</div>}
                      </div>
                      <div className="col-sm-6">
                        <label htmlFor="address" className="h5 mb-8 fw-semibold font-heading">
                          Address <span className="text-13 text-red-500 fw-medium">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control py-11"
                          id="address"
                          placeholder="Enter Address"
                          value={formData.address}
                          onChange={handleChange}
                          required
                          disabled={isLoading}
                        />
                        {errors.address && <div className="text-danger text-13">{errors.address}</div>}
                      </div>
                      <div className="col-sm-6">
                        <label htmlFor="postcode" className="h5 mb-8 fw-semibold font-heading">
                          Postcode
                        </label>
                        <input
                          type="text"
                          className="form-control py-11"
                          id="postcode"
                          placeholder="Enter Postcode"
                          value={formData.postcode}
                          onChange={handleChange}
                          disabled={isLoading}
                        />
                      </div>
                      <div className="col-sm-6">
                        <label htmlFor="mainTelephone" className="h5 mb-8 fw-semibold font-heading">
                          Main Telephone No.
                        </label>
                        <input
                          type="tel"
                          className="form-control py-11"
                          id="mainTelephone"
                          placeholder="Enter Telephone Number"
                          value={formData.mainTelephone}
                          onChange={handleChange}
                          disabled={isLoading}
                        />
                      </div>
                      <div className="col-sm-6">
                        <label htmlFor="degree" className="h5 mb-8 fw-semibold font-heading">
                          Degree
                        </label>
                        <select
                          id="degree"
                          className="form-select py-9 placeholder-13 text-15"
                          value={formData.degree}
                          onChange={handleChange}
                          disabled={isLoading}
                        >
                          <option value="" disabled>Select an answer</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      </div>
                      <div className="col-sm-6">
                        <label htmlFor="engineering" className="h5 mb-8 fw-semibold font-heading">
                          Engineering
                        </label>
                        <select
                          id="engineering"
                          className="form-select py-9 placeholder-13 text-15"
                          value={formData.engineering}
                          onChange={handleChange}
                          disabled={isLoading}
                        >
                          <option value="" disabled>Select an answer</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      </div>
                      <div className="col-sm-6">
                        <label htmlFor="currentStatus" className="h5 mb-8 fw-semibold font-heading">
                          Current Status
                        </label>
                        <select
                          id="currentStatus"
                          className="form-select py-9 placeholder-13 text-15"
                          value={formData.currentStatus}
                          onChange={handleChange}
                          disabled={isLoading}
                        >
                          <option value="" disabled>Select status</option>
                          <option value="Employed">Employed</option>
                          <option value="Not Employed">Not Employed</option>
                          <option value="Student">Student</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="col-xxl-12">
                    <div className="row g-20">
                      <div className="col-sm-6">
                        <label htmlFor="preferableCourse" className="h5 mb-8 fw-semibold font-heading">
                          Preferable Course
                        </label>
                        <select
                          id="preferableCourse"
                          className="form-select py-9 placeholder-13 text-15"
                          value={formData.preferableCourse}
                          onChange={handleChange}
                          disabled={isLoading || !courses.length}
                        >
                          <option value="" disabled>Select a course</option>
                          {courses.map((course) => (
                            <option key={course.id} value={course.id}>
                              {course.coursename}
                            </option>
                          ))}
                        </select>
                        {apiError && !courses.length && <div className="text-danger text-13">{apiError}</div>}
                      </div>
                      <div className="col-sm-6">
                        <label htmlFor="batch" className="h5 mb-8 fw-semibold font-heading">
                          Batch
                        </label>
                        <select
                          id="batch"
                          className="form-select py-9 placeholder-13 text-15"
                          value={formData.batch}
                          onChange={handleChange}
                          disabled={isLoading || !formData.preferableCourse || !batches.length}
                        >
                          <option value="" disabled>
                            {batches.length ? 'Select a batch' : 'No batches available'}
                          </option>
                          {batches.map((batch) => (
                            <option key={batch.id} value={batch.id}>
                              {batch.batchname}
                            </option>
                          ))}
                        </select>
                        {apiError && formData.preferableCourse && !batches.length && (
                          <div className="text-danger text-13">{apiError}</div>
                        )}
                      </div>
                      <div className="col-sm-6">
                        <label htmlFor="username" className="h5 mb-8 fw-semibold font-heading">
                          Username
                        </label>
                        <input
                          type="text"
                          className="form-control py-11"
                          id="username"
                          placeholder="Enter Username"
                          value={formData.username}
                          onChange={handleChange}
                          disabled={isLoading}
                        />
                      </div>
                      <div className="col-sm-6">
                        <label htmlFor="password" className="h5 mb-8 fw-semibold font-heading">
                          Password
                        </label>
                        <input
                          type="password"
                          className="form-control py-11"
                          id="password"
                          placeholder="Enter Password"
                          value={formData.password}
                          onChange={handleChange}
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex-align justify-content-end gap-8">
                    <Link to="/admin-dashboard" className="btn btn-outline-main rounded-pill py-9">
                      Cancel
                    </Link>
                    <button
                      type="submit"
                      className="btn btn-main rounded-pill py-9"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Submitting...' : 'Submit Student'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}
export default AdminAddStudent;